import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useImperativeHandle } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import {
  investmentHoldingsV2Actions,
  investmentHoldingsV2Selectors,
  investmentHoldingsV2Types,
} from 'app-components/flux/investment-holdings-v2';
import { investmentQuotesV2Selectors } from 'app-components/flux/investment-quotes-v2';
import {
  securitiesActions,
  securitiesSelectors,
  securitiesTypes,
  securitiesUtils,
} from 'app-components/flux/securities';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useTheme } from '@mui/material/styles';

import Typography from '@mui/material/Typography';
import Dump from 'components/Dump';
import QAmountField from 'components/QAmountField';

const securityTypes = [
  securitiesTypes.SecurityTypeEnum.STOCK,
  securitiesTypes.SecurityTypeEnum.MUTUALFUND,
  securitiesTypes.SecurityTypeEnum.DEBT,
  securitiesTypes.SecurityTypeEnum.OPTION,
  securitiesTypes.SecurityTypeEnum.BOND,
  securitiesTypes.SecurityTypeEnum.CRYPTOCURRENCY,
  securitiesTypes.SecurityTypeEnum.OTHER,
];

const SecurityEditForm = React.memo((props) => {
  const { defaultValue: security, onClose, setDisableSubmit, onDirty, formikRef, ...rest } = props;

  const theme = useTheme();
  const manualHoldings = useSelector((state) =>
    investmentHoldingsV2Selectors.getManualHoldingsWithLocalSecurityId(state, security?.id),
  );
  const doesQuoteExist = Boolean(
    useSelector((state) => investmentQuotesV2Selectors.getInvestmentQuoteBySymbol(state, security?.symbol || null)),
  );
  const quote = useSelector((state) => securitiesSelectors.getQuoteByLocalSecurityId(state, security?.id));
  const quotePrice = securitiesUtils.getPrice(quote);

  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      ...security,
      currentUnitPrice: quotePrice,
      duplicateSymbol: false,
      duplicateName: false,
    },
    onSubmit: (values) => {
      const payload = securitiesTypes.mkSecurity({
        clientId: uuidv4().toUpperCase(),
        securityId: uuidv4().toUpperCase(),
        ...values,
      });
      if (values.id) {
        if (manualHoldings?.size) {
          dispatch(
            investmentHoldingsV2Actions.batchInvestmentHoldingsV2(
              manualHoldings.map((holding) =>
                investmentHoldingsV2Types.mkInvestmentHoldingV2({
                  ...(holding.toJS?.() || holding),
                  currentPrice: Number(values.currentUnitPrice),
                  marketValue: holding.quantity * Number(values.currentUnitPrice),
                }),
              ),
            ),
          );
        }
        dispatch(securitiesActions.updateSecurity(payload, { undo: { userMessage: 'Security updated.' } }));
      } else {
        dispatch(securitiesActions.createSecurity(payload, { undo: { userMessage: 'Security created.' } }));
      }

      onClose?.(undefined, 'submit');
    },
  });

  const doesNameExistSelector = useSelector((state) =>
    securitiesSelectors.getSecurityBySymbolOrName(state, undefined, formik?.values?.name?.trim()),
  );
  const doesSymbolExistSelector = useSelector((state) =>
    securitiesSelectors.getSecurityBySymbolOrName(state, formik?.values?.symbol?.trim(), undefined),
  );

  const doesNameExist = useSelector(securitiesSelectors.getResourcesById);

  useEffect(() => {
    formik.registerField('securityType', { validate: (value) => (value ? undefined : 'required') });
    formik.registerField('symbol', { validate: () => undefined });
    formik.registerField('name', { validate: (value) => (value || formik.values.name ? undefined : 'required') });
    formik.registerField('securityIdType', { validate: (value) => (value ? undefined : 'required') });
    return () => {
      formik.unregisterField('symbol');
      formik.unregisterField('securityType');
      formik.unregisterField('name');
      formik.unregisterField('securityIdType');
    };
  }, [formik]);

  useEffect(() => {
    if (security?.id) {
      onDirty?.(formik.dirty);
    }
  }, [formik.dirty, onDirty, security?.id]);

  useEffect(() => {
    if (formik.values.duplicateName || formik.values.duplicateSymbol) {
      setDisableSubmit(true);
    } else {
      setDisableSubmit(false);
    }
  }, [formik, setDisableSubmit]);

  useImperativeHandle(formikRef, () => formik, [formik]);

  return (
    <form onSubmit={formik.handleSubmit} {...rest}>
      <Stack gap={3} position='relative'>
        <Dump obj={{ formik, security }} />
        <Stack gap={3} flexDirection='row'>
          <TextField
            sx={{ flex: 1 }}
            autoFocus
            variant='filled'
            label='Symbol'
            value={formik.values.symbol}
            onChange={(event) => {
              // dont allow for duplciate symbols and updating a security to be a duplicate symbol
              if (
                doesNameExist.find((p) => p.symbol === event.target.value.trim()) !== undefined &&
                (!security?.id || (security?.id && event.target.value?.trim() !== security?.symbol))
              ) {
                formik.setFieldValue('duplicateSymbol', true);
              } else {
                formik.setFieldValue('duplicateSymbol', false);
              }
              formik.setFieldValue('symbol', event.target.value);
            }}
            error={
              Boolean(formik.submitCount > 0 && formik.errors.symbol) ||
              (doesSymbolExistSelector && formik.values.duplicateSymbol)
            }
            helperText={
              (doesSymbolExistSelector && formik.values.duplicateSymbol && 'Already in use, choose a unique symbol') ||
              (formik.submitCount > 0 && formik.errors.symbol) ||
              ''
            }
          />
          <TextField
            sx={{ flex: 4 }}
            variant='filled'
            label='Name'
            value={formik.values.name}
            onChange={(event) => {
              // dont allow for empty security names, duplciate names and updating a security to be a duplicate name
              if (
                event.target.value.trim().length === 0 ||
                (doesNameExist.find((p) => p.name === event.target.value.trim()) !== undefined &&
                  (!security?.id || (security?.id && event.target.value?.trim() !== security?.name)))
              ) {
                formik.setFieldValue('duplicateName', true);
              } else {
                formik.setFieldValue('duplicateName', false);
              }
              formik.setFieldValue('name', event.target.value);
            }}
            error={
              Boolean(formik.submitCount > 0 && formik.errors.name) ||
              formik.values.name?.trim().length === 0 ||
              (doesNameExistSelector && formik.values.duplicateName)
            }
            helperText={
              (doesNameExistSelector && formik.values.duplicateName && 'Already in use, choose a unique name') ||
              (formik.values.name?.trim().length === 0 && 'Name cannot be blank and is required') ||
              (formik.submitCount > 0 && formik.errors.name) ||
              ''
            }
          />
        </Stack>
        <Stack gap={3} flexDirection='row'>
          {!doesQuoteExist ? (
            <QAmountField
              wrapperStyles={{ flex: 1 }}
              textFieldProps={{
                sx: { width: '100%' },
                error: Boolean(formik.submitCount > 0 && formik.errors.currentUnitPrice),
                helperText: (formik.submitCount > 0 && formik.errors.currentUnitPrice) || '',
              }}
              variant='filled'
              editable
              showSign={false}
              expectedSign='positive'
              label='Price'
              disabled={doesQuoteExist}
              decimals={4}
              value={formik.values.currentUnitPrice}
              onChange={(event) => formik.setFieldValue('currentUnitPrice', event.target.value)}
            />
          ) : (
            <Box display='flex' flexDirection='column' marginLeft={1}>
              <Typography sx={{ color: theme.palette.text.secondary }} variant='paragraphRegular'>
                Price
              </Typography>
              <QAmountField
                sx={{
                  textAlign: 'left',
                  whiteSpace: 'pre-wrap',
                  width: 325,
                }}
                fractionCents
                decimals={4}
                value={formik.values.currentUnitPrice}
                onChange={undefined}
                submitOnBlur={undefined}
                expectedSign='positive'
                editable={false}
                variant='bodyRegular'
                align='left'
                id={'price-disabled'}
              />
            </Box>
          )}
          <Autocomplete
            sx={{ flex: 1 }}
            id='security-type-autocomplete'
            autoComplete
            clearOnEscape
            clearIcon={null}
            autoHighlight
            autoSelect
            value={formik.values.securityType}
            onChange={(_event, value, _reason, _details) => formik.setFieldValue('securityType', value)}
            options={securityTypes}
            getOptionLabel={(value) => securitiesUtils.getSecurityTypeString(value)}
            renderInput={(params) => <TextField {...params} label='Type' variant='filled' />}
            error={Boolean(formik.submitCount > 0 && formik.errors.securityType)}
            helperText={(formik.submitCount > 0 && formik.errors.securityType) || ''}
          />
        </Stack>
      </Stack>
    </form>
  );
});

SecurityEditForm.propTypes = {
  defaultValue: PropTypes.object,
  formikRef: PropTypes.object,
  onClose: PropTypes.func,
  onDirty: PropTypes.func,
  setDisableSubmit: PropTypes.func,
};

SecurityEditForm.displayName = 'SecurityEditForm';

export default SecurityEditForm;
